import {
  formatCurrency,
  formatMaybeCurrency,
  formatPercent,
  formatPercentagePoints,
  formatSplitPercent,
} from '../SharedAccountBody';
import { FinIntellectModelCode } from '../FinIntellectModelCode';
import { BENCHMARK_COLOR } from './comparisonTypes';
import type { ComparisonAccount } from './comparisonTypes';
import ComparisonAccountModeBadge from './ComparisonAccountModeBadge';
import ComparisonHoverTooltip from './ComparisonHoverTooltip';
import ComparisonSignalForgeBadge from './ComparisonSignalForgeBadge';
import { isComparisonContentLoading } from './comparisonLoading';
import ComparisonPerformanceTableSkeleton from './ComparisonPerformanceTableSkeleton';
import {
  portfolioModelTooltipContent,
  sinceStartRowTooltipContent,
  vsLeaderColumnTooltipContent,
} from './comparisonTooltipContent';
import {
  benchmarkSummaryFromAccounts,
  performanceSummary,
} from './comparisonUtils';

const toneClass = (value?: number) =>
  value === undefined || value === 0
    ? ''
    : value > 0
      ? 'text-positive'
      : 'text-negative';

const CARD_CLASS =
  'rounded-[12px] border border-[color:var(--nq-line)] bg-[color:var(--nq-surface-card)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

const TH_BASE =
  'border-b border-white/[0.08] px-2.5 py-2 align-bottom text-[0.78rem] font-semibold uppercase tracking-[0.04em] text-white/60';
const TH_LABEL = `${TH_BASE} relative w-auto min-w-[11rem] max-w-[14rem] overflow-visible text-left`;
const TH_NUM = `${TH_BASE} w-auto min-w-[5.25rem] whitespace-normal leading-[1.25] text-right tabular-nums`;
const TH_SINCE = `${TH_NUM} min-w-[5.5rem]`;

const TD_LABEL =
  'relative w-auto min-w-[11rem] max-w-[14rem] overflow-visible whitespace-normal px-2.5 py-2 align-top text-left';
const TD_NUM =
  'w-auto min-w-[5.25rem] whitespace-nowrap px-2.5 py-2 align-top text-right tabular-nums';

const TD_SUB = 'mt-px text-[0.78rem] text-white/55';
const CHIP_DOT = 'inline-block h-2 w-2 shrink-0 rounded-full';
const CHIP_LABEL = 'font-medium';

const TAG_NUM =
  'inline-block whitespace-nowrap rounded-[6px] border px-[7px] py-0.5 text-[0.68rem] font-semibold uppercase leading-[1.3] tracking-[0.06em]';
const TAG_LEADER =
  'bg-secondary/14 border-secondary/42 text-[color:#ffc580]';
const TAG_BENCHMARK =
  'bg-[color:rgba(123,135,152,0.16)] border-[color:rgba(123,135,152,0.4)] text-[color:#b8c4d0]';

const formatPctOrDash = (value?: number) =>
  value === undefined ? '—' : formatPercent(value);

const formatPpOrDash = (value?: number) =>
  value === undefined ? '—' : formatPercentagePoints(value);

const formatSignedAmountOrDash = (value?: number) =>
  value === undefined
    ? '—'
    : `${value >= 0 ? '+' : ''}${formatCurrency(value)}`;

const PerformanceCell = ({
  pct,
  amount,
}: {
  pct?: number;
  amount?: number;
}) => (
  <>
    <div>{formatPctOrDash(pct)}</div>
    <div className={TD_SUB}>{formatSignedAmountOrDash(amount)}</div>
  </>
);

const ComparisonPerformanceTable = ({
  accounts,
}: {
  accounts: ComparisonAccount[];
}) => {
  if (accounts.length === 0) return null;

  if (isComparisonContentLoading(accounts)) {
    return (
      <ComparisonPerformanceTableSkeleton
        rows={accounts.length + 1}
        columns={9}
      />
    );
  }

  const summaries = accounts.map((a) => ({
    account: a,
    summary: performanceSummary(a.content),
  }));

  const benchmark = benchmarkSummaryFromAccounts(accounts);

  const leaderSinceStartPct = summaries.reduce<number | undefined>((max, s) => {
    if (s.summary.sinceStartPct === undefined) return max;
    return max === undefined || s.summary.sinceStartPct > max
      ? s.summary.sinceStartPct
      : max;
  }, undefined);

  const leaderAlphaSinceStart = summaries.reduce<number | undefined>((max, s) => {
    if (s.summary.alphaSinceStart === undefined) return max;
    return max === undefined || s.summary.alphaSinceStart > max
      ? s.summary.alphaSinceStart
      : max;
  }, undefined);

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-col">
        <h3 className="text-[1.05rem] font-semibold text-foreground">
          Performance breakdown
        </h3>
        <p className="mt-1 text-[0.85rem] text-white/55">
          Side by side vs. S&amp;P 500 (SPY), with return and alpha deltas vs. the leader.
        </p>
      </div>

      <div className="overflow-x-auto overflow-y-visible [scrollbar-width:thin]">
        <table className="w-max min-w-full table-auto border-collapse text-[14px]">
          <thead>
            <tr>
              <th className={TH_LABEL}>Portfolio</th>
              <th className={TH_NUM}>Total value</th>
              <th
                className={TH_SINCE}
                title="Total return since trading begins"
              >
                Total return
              </th>
              <th className={TH_NUM}>Today</th>
              <th className={TH_NUM}>Last 30d</th>
              <th className={TH_NUM}>Alpha</th>
              <th className={TH_NUM}>
                <ComparisonHoverTooltip
                  placement="bottom"
                  alignEnd
                  content={vsLeaderColumnTooltipContent()}
                >
                  <span className="cursor-help border-0 border-b border-dotted border-white/25">
                    vs leader
                  </span>
                </ComparisonHoverTooltip>
              </th>
              <th className={TH_NUM}>Invested</th>
              <th className={TH_NUM}>Cash</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map(({ account, summary }) => {
              const isLeader =
                leaderSinceStartPct !== undefined &&
                summary.sinceStartPct === leaderSinceStartPct;
              const returnDelta =
                leaderSinceStartPct !== undefined &&
                summary.sinceStartPct !== undefined
                  ? summary.sinceStartPct - leaderSinceStartPct
                  : undefined;
              const alphaDelta =
                leaderAlphaSinceStart !== undefined &&
                summary.alphaSinceStart !== undefined
                  ? summary.alphaSinceStart - leaderAlphaSinceStart
                  : undefined;
              return (
                <tr
                  key={account.accountId}
                  className="border-b border-white/[0.04] last:border-b-0"
                >
                  <td className={TD_LABEL}>
                    <ComparisonHoverTooltip
                      block
                      placement="bottom"
                      content={portfolioModelTooltipContent(
                        account.account,
                        account.content,
                      )}
                    >
                      <div className="flex flex-col items-start gap-1 whitespace-normal font-medium">
                        <div className="inline-flex flex-wrap items-center gap-1.5">
                          <span
                            className={CHIP_DOT}
                            style={{ backgroundColor: account.color }}
                            aria-hidden
                          />
                          <span className={CHIP_LABEL}>{account.label}</span>
                          <ComparisonAccountModeBadge account={account.account} />
                        </div>
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          <FinIntellectModelCode
                            model={account.account.finintellectModel}
                            className="px-[5px] py-px text-[10px]"
                          />
                          <ComparisonSignalForgeBadge
                            enabled={!!account.account.signal_forge_enabled}
                          />
                        </span>
                      </div>
                    </ComparisonHoverTooltip>
                  </td>
                  <td className={TD_NUM}>
                    {formatMaybeCurrency(summary.totalValue)}
                  </td>
                  <td className={`${TD_NUM} ${toneClass(summary.sinceStartPct)}`}>
                    <ComparisonHoverTooltip
                      placement="bottom"
                      alignEnd
                      content={sinceStartRowTooltipContent(account, summary)}
                    >
                      <span className="block">
                        <PerformanceCell
                          pct={summary.sinceStartPct}
                          amount={summary.sinceStartAmount}
                        />
                      </span>
                    </ComparisonHoverTooltip>
                  </td>
                  <td className={`${TD_NUM} ${toneClass(summary.todayPct)}`}>
                    <PerformanceCell
                      pct={summary.todayPct}
                      amount={summary.todayAmount}
                    />
                  </td>
                  <td className={`${TD_NUM} ${toneClass(summary.lastMonthPct)}`}>
                    <PerformanceCell
                      pct={summary.lastMonthPct}
                      amount={summary.lastMonthAmount}
                    />
                  </td>
                  <td className={`${TD_NUM} ${toneClass(summary.alphaSinceStart)}`}>
                    <div>{formatPctOrDash(summary.alphaSinceStart)}</div>
                    {summary.alphaLastMonth !== undefined && (
                      <div className={TD_SUB}>
                        30d {formatPctOrDash(summary.alphaLastMonth)}
                      </div>
                    )}
                    {summary.alphaToday !== undefined && (
                      <div className={TD_SUB}>
                        today{' '}
                        {summary.marketOpen === false
                          ? '—'
                          : formatPctOrDash(summary.alphaToday)}
                      </div>
                    )}
                  </td>
                  <td className={TD_NUM}>
                    {isLeader ? (
                      <span className={`${TAG_NUM} ${TAG_LEADER}`}>Leader</span>
                    ) : returnDelta === undefined && alphaDelta === undefined ? (
                      '—'
                    ) : (
                      <ComparisonHoverTooltip
                        placement="bottom"
                        alignEnd
                        content={vsLeaderColumnTooltipContent()}
                      >
                        <span className="inline-block text-right">
                          {returnDelta !== undefined && (
                            <div
                              className={`font-medium ${toneClass(returnDelta)}`}
                            >
                              Δ {formatPpOrDash(returnDelta)}
                            </div>
                          )}
                          {alphaDelta !== undefined && (
                            <div className={`${TD_SUB} ${toneClass(alphaDelta)}`}>
                              α {formatPpOrDash(alphaDelta)}
                            </div>
                          )}
                        </span>
                      </ComparisonHoverTooltip>
                    )}
                  </td>
                  <td className={TD_NUM}>
                    <div>{formatMaybeCurrency(summary.invested)}</div>
                    <div className={TD_SUB}>
                      {summary.investedPct !== undefined
                        ? formatSplitPercent(summary.investedPct)
                        : '—'}
                    </div>
                  </td>
                  <td className={TD_NUM}>
                    <div>{formatMaybeCurrency(summary.cash)}</div>
                    <div className={TD_SUB}>
                      {summary.cashPct !== undefined
                        ? formatSplitPercent(summary.cashPct)
                        : '—'}
                    </div>
                  </td>
                </tr>
              );
            })}
            {benchmark && (
              <tr className="border-t border-white/[0.08] bg-white/[0.015]">
                <td className={TD_LABEL}>
                  <div className="inline-flex items-center gap-2 whitespace-nowrap font-medium">
                    <span
                      className={CHIP_DOT}
                      style={{ backgroundColor: BENCHMARK_COLOR }}
                      aria-hidden
                    />
                    <span className={CHIP_LABEL}>{benchmark.label}</span>
                  </div>
                </td>
                <td className={TD_NUM}>—</td>
                <td className={`${TD_NUM} ${toneClass(benchmark.sinceStartPct)}`}>
                  <PerformanceCell
                    pct={benchmark.sinceStartPct}
                    amount={benchmark.sinceStartAmount}
                  />
                </td>
                <td className={`${TD_NUM} ${toneClass(benchmark.todayPct)}`}>
                  <PerformanceCell
                    pct={benchmark.todayPct}
                    amount={benchmark.todayAmount}
                  />
                </td>
                <td className={`${TD_NUM} ${toneClass(benchmark.lastMonthPct)}`}>
                  <PerformanceCell
                    pct={benchmark.lastMonthPct}
                    amount={benchmark.lastMonthAmount}
                  />
                </td>
                <td className={TD_NUM}>—</td>
                <td className={TD_NUM}>
                  <span className={`${TAG_NUM} ${TAG_BENCHMARK}`}>Benchmark</span>
                </td>
                <td className={TD_NUM}>—</td>
                <td className={TD_NUM}>—</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonPerformanceTable;
